<template>
  <div>
    <div
      class="mx-auto flex flex-col gap-5 md:flex-row md:items-center"
      :class="{ 'max-w-container': !isLogsTab && !isInEditor }"
    >
      <div v-if="!hideHeaderIpPort">
        <strong class="mr-1">{{ copyAccessLabel }}:</strong>
        <clipboard-copy
          :message="!isRunning ? t('ngp/clipboard/copy/access/server_offline') : undefined"
          :content="copyAccessData"
          :duration="3000"
          :masked="enhancedPrivacyMode"
          copy-on="all"
          icon-mode="hover"
          data-testid="header-server-ip"
        >
          <span class="break-words">{{ accessData }}</span>
        </clipboard-copy>
      </div>
      <div v-show="webDashboardIp">
        <strong class="mr-1">{{ t("ngp/header/webDashboardIp") }}:</strong>
        <clipboard-copy
          :content="webDashboardIp"
          :duration="3000"
          copy-on="all"
          icon-mode="hover"
          :masked="enhancedPrivacyMode"
        >
          <span class="break-words">{{ webDashboardIp }}</span>
        </clipboard-copy>
      </div>
      <div v-if="gameUID">
        <strong> {{ t("ngp/header/info/game_uid") }}: </strong>
        <clipboard-copy icon-mode="hover" copy-on="all" :content="gameUID"></clipboard-copy>
      </div>
      <div v-if="serverRoomCode">
        <strong> {{ t("ngp/header/info/server_room_code") }}: </strong>
        <clipboard-copy icon-mode="hover" copy-on="all" :content="serverRoomCode"></clipboard-copy>
      </div>
      <div v-if="joinCode">
        <strong> {{ t("ngp/header/info/join_code") }}: </strong>
        <clipboard-copy icon-mode="hover" copy-on="all" :content="joinCode"></clipboard-copy>
        <gp-tooltip v-if="joinCodeSinceHint" :content="joinCodeSinceHint">
          <font-awesome-icon
            :icon="faQuestionCircle"
            class="text-gp-rhino ml-1 cursor-pointer"
            :aria-label="joinCodeSinceHint"
          />
        </gp-tooltip>
      </div>
      <div v-if="inviteCode">
        <strong> {{ t("ngp/header/info/invite_code") }}: </strong>
        <clipboard-copy icon-mode="hover" copy-on="all" :content="inviteCode"></clipboard-copy>
      </div>
      <div class="flex justify-between" :class="{ 'md:flex-1': !joinFeature }">
        <span v-if="showActiveMap">
          <strong> {{ t("ngp/header/info/active_map") }}: </strong>
          <span v-if="queried && activeMap"> {{ activeMap }} </span>
          <span v-else> — </span>
        </span>
        <span v-else-if="showActivePlayers">
          <strong> {{ t("ngp/header/info/active_players") }}: </strong>
          <span v-if="queried"> {{ currentPlayers }}/{{ maxPlayers }} </span>
          <span v-else> {{ isRunning ? "0" : "?" }}/{{ maxPlayers || "?" }} </span>
        </span>
      </div>
      <div v-if="showTxAdminLink">
        <gp-navigation-item-link v-if="isRunning" :href="txAdminUrl" class="flex items-center gap-1" target="_blank">
          <font-awesome-icon :icon="faArrowUpRightFromSquare" class="text-gp-rhino text-sm" />
          <span class="font-bold uppercase tracking-wider">{{ t("ngp/header/txadmin/link") }}</span>
        </gp-navigation-item-link>
        <span v-else>{{ t("ngp/header/txadmin/offline") }}</span>
      </div>
      <div v-if="showWindrosePlusDashboard">
        <gp-navigation-item-link
          v-if="isRunning"
          :href="windrosePlusDashboardUrl"
          class="flex items-center gap-1"
          target="_blank"
        >
          <font-awesome-icon :icon="faArrowUpRightFromSquare" class="text-gp-rhino text-sm" />
          <span class="font-bold uppercase tracking-wider">{{ t("ngp/header/windrose_plus/link") }}</span>
        </gp-navigation-item-link>
        <span v-else>{{ t("ngp/header/windrose_plus/offline") }}</span>
      </div>
      <div class="flex flex-col gap-2">
        <div v-if="isFivem || isRedm" class="md:flex-1">
          <gp-btn-link
            :label="t('ngp/header/action/join')"
            :icon="faGamepad"
            :href="isFivem ? fiveMJoinLink : redMJoinLink"
            size="small"
          ></gp-btn-link>
        </div>
        <div v-if="hasJoinPopup" class="md:flex-1">
          <gp-btn :label="t('ngp/server/join_tutorial/button/label')" size="small" @click="howToJoin()"></gp-btn>
        </div>
        <div v-if="extraHeaderButton" class="md:flex-1">
          <gp-btn :label="t(extraHeaderButton.buttonTextKey)" size="small" @click="extra()"></gp-btn>
        </div>
      </div>
      <hr class="border-gp-rhino/50 border md:hidden" />
      <div class="flex items-center justify-between gap-5 md:ml-auto">
        <gp-switch
          input-id="select-dune-battlegroup"
          v-if="isDune && IsEmptyBattlegroup"
          v-model="shouldBeRunning"
          :label="stateLabel"
          :disabled="IsEmptyBattlegroup"
          @click="selectBattlegroup()"
        />
        <gp-switch
          v-else
          input-id="start-server-toggle"
          v-model="shouldBeRunning"
          :label="stateLabel"
          :disabled="commitInProgress || isSuspended"
          data-testid="start-server-toggle"
        />
        <gp-btn
          v-if="isBroadcasting && isAdmin"
          size="small"
          :label="t('ngp/action/skip_broadcast/title')"
          :icon="faTriangleExclamation"
          @click="service.skipBroadcast()"
        />
        <div>
          <gp-btn
            :disabled="!canRestart || commitInProgress"
            size="small"
            data-testid="button-server-restart"
            :label="t('ngp/action/restart/title')"
            @click="service.restartService(rsid)"
          ></gp-btn>
        </div>
      </div>
    </div>
    <div
      v-if="showAloftHelpText"
      class="mx-auto flex flex-col gap-5 md:flex-row md:items-center"
      :class="{ 'max-w-container': !isLogsTab && !isInEditor }"
    >
      <span>{{ t("ngp/header/info/aloft/offline") }}</span>
    </div>
    <div
      v-if="showAbioticFactorHelpText"
      class="mx-auto flex flex-col gap-5 md:flex-row md:items-center"
      :class="{ 'max-w-container': !isLogsTab && !isInEditor }"
    >
      <span>{{ t("ngp/header/info/abiotic_factor/offline") }}</span>
    </div>
    <div
      v-if="showVoyagersOfNeraHelpText"
      class="mx-auto flex flex-col gap-5 md:flex-row md:items-center"
      :class="{ 'max-w-container': !isLogsTab && !isInEditor }"
    >
      <span>{{ t("ngp/header/info/voyagersOfNera/offline") }}</span>
    </div>
    <div
      v-if="showValheimJoinCodeHelpText"
      class="mx-auto flex flex-col gap-5 md:flex-row md:items-center"
      :class="{ 'max-w-container': !isLogsTab && !isInEditor }"
    >
      <span>{{ t("ngp/header/info/valheim/join_code_pending") }}</span>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useDialogStore } from "@/store/dialog";
import useUserPrivacyModes from "@/util/useUserPrivacyModes";
import {
  faArrowUpRightFromSquare,
  faGamepad,
  faQuestionCircle,
  faTriangleExclamation,
} from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";
import { GpBtn, GpBtnLink, GpNavigationItemLink, GpSwitch, GpTooltip } from "@gportal/uikit";
import { DateTime } from "luxon";
import { computed, defineAsyncComponent, markRaw, watch } from "vue";
import { useI18n } from "vue-i18n";
import { useRoute } from "vue-router";
import { Service_States } from "../../graphql";
import { useClientStore } from "../../store/client";
import { useServiceStore } from "../../store/service";
import { useAccessData } from "../../util/accessData";
import { usePermissions } from "../../util/permissions";
import useServiceState from "../../util/serviceState";
import ClipboardCopy from "../util/ClipboardCopy.vue";

const service = useServiceStore();
const client = useClientStore();
const { t, locale } = useI18n();
const { hasDns, accessData, copyAccessData } = useAccessData();
const route = useRoute();
const installedBattlegroup = computed(() => service.ctx.changeTracking?.publicConfigs?.virtual?.battlegroups ?? null);
const IsEmptyBattlegroup = computed(() => !installedBattlegroup.value || installedBattlegroup.value === "");

const currentTab = computed(() => route.params.tabSegments);
const isLogsTab = computed(() => currentTab.value[0] === "logs");
const { stateLabel, canRestart, isSuspended } = useServiceState();

const commitInProgress = computed(() => service.commitInProgress);
const isInEditor = computed(() => client.isEditorOpened);

const currentState = computed(() => service.ctx.service?.currentState);
const fsmState = computed(() => currentState.value?.fsmState);
const isBroadcasting = computed(() => fsmState.value === "BroadcastingShutdown");
const serviceNamespace = computed(() => service.ctx.service);
const gameProfile = computed(() => service.ctx.gameProfile);
const gameUID = computed(() => service.ctx.profile?.gameUid);
const serverRoomCode = computed(() => service.ctx.profile?.serverRoomCode);
const joinCode = computed(() => service.ctx.profile?.joinCode);
const inviteCode = computed(() => service.ctx.profile?.inviteCode);
const isFivem = computed(() => service.ctx.profile?.name === "FiveM");
const isRedm = computed(() => service.ctx.profile?.name === "RedM");
const hasJoinPopup = computed(
  () =>
    service.ctx.profile?.name === "Minecraft" ||
    service.ctx.profile?.name === "Nightingale" ||
    service.ctx.profile?.name === "Hytale" ||
    service.ctx.profile?.name === "StarRupture",
);
const hideHeaderIpPort = computed(() => service.ctx.gameProfile?.hideHeaderIpPort ?? false);
const isDune = computed(() => service.ctx.gameProfile?.hasBattlegroups ?? false);
const isRunning = computed(() => serviceNamespace.value?.currentState?.state === Service_States.Running);
const rsid = computed(() => serviceNamespace.value?.config?.rsid).value;
const copyAccessLabel = computed(() =>
  hasDns.value ? t("ngp/status/access/common/server_address") : t("ngp/status/access/common/ip"),
);
const joinFeature = computed(() => gameProfile.value?.join);
const extraHeaderButton = computed(() => gameProfile.value?.extraHeaderButton);
const showActivePlayers = computed(() => service.ctx.gameProfile?.hasActivePlayers);
const showActiveMap = computed(() => service.ctx.gameProfile?.hasActiveMap);
const showAloftHelpText = computed(() => service.ctx.profile?.name === "Aloft" && !serverRoomCode.value);
const showAbioticFactorHelpText = computed(() => service.ctx.profile?.name === "AbioticFactor" && !joinCode.value);
const showVoyagersOfNeraHelpText = computed(() => service.ctx.profile?.name === "VoyagersOfNera" && !joinCode.value);
// "Valheim" is the PC profile, "ValheimUwp" the Xbox one - both offer the crossplay switch.
const isValheim = computed(() => ["Valheim", "ValheimUwp"].includes(service.ctx.profile?.name ?? ""));
// Crossplay is on but the server has not registered with the PlayFab relay yet (or is not running),
// so there is no code to hand out. Steam-only Valheim servers never get one at all and say nothing:
// crossplay is false there, and the row above is hidden either way.
const showValheimJoinCodeHelpText = computed(
  () => isValheim.value && !!service.ctx.profile?.crossplay && !joinCode.value,
);
/**
 * "Unchanged since <date>" for the join code, or nothing if the daemon could not date it.
 *
 * Valheim's join code is neither permanent nor per-restart, whatever the game's own FAQ says:
 * production logs show codes surviving 30+ relay re-registrations over weeks, and also rotating
 * with no restart and an unchanged IP:port. So the interface states the one thing it actually
 * knows - how long this code has been in use - and says "at least" whenever the log was truncated
 * before the code first appeared, which is the common case.
 */
const joinCodeSinceHint = computed(() => {
  const since = service.ctx.profile?.joinCodeSince;
  if (!since) return null;

  const dt = DateTime.fromISO(since).setLocale(locale.value);
  if (!dt.isValid) return null;

  const date = dt.toLocaleString(DateTime.DATETIME_MED);
  return service.ctx.profile?.joinCodeSinceExact
    ? t("ngp/header/info/join_code/unchanged_since", { date })
    : t("ngp/header/info/join_code/unchanged_since_at_least", { date });
});
const showTxAdminLink = computed(() => service.ctx.profile?.txAdmin?.enabled);
const txAdminUrl = computed(
  () => "http://" + service.ctx.service?.config?.ipAddress + ":" + service.ctx.profile?.txAdmin?.port,
);
const wpDashboardPort = computed(() => service.ctx.profile?.displayPorts?.wpDashboardPort);
const showWindrosePlusDashboard = computed(() => {
  const val = service.ctx.changeTracking?.publicConfigs?.virtual?.windrose_plus_enabled;
  return wpDashboardPort.value && (val === true || val === "true");
});
const windrosePlusDashboardUrl = computed(
  () => "http://" + service.ctx.service?.config?.ipAddress + ":" + wpDashboardPort.value,
);
const fiveMJoinLink = computed(
  () => "fivem://connect/" + service.ctx.service?.config?.ipAddress + ":" + service.ctx?.sys?.gameServer.serverPort,
);
const redMJoinLink = computed(
  () => "redm://connect/" + service.ctx.service?.config?.ipAddress + ":" + service.ctx?.sys?.gameServer.serverPort,
);

const { isAdmin } = usePermissions();
const { enhancedPrivacyMode } = useUserPrivacyModes();

const shouldBeRunning = computed({
  get(): boolean {
    return serviceNamespace.value?.config?.state === Service_States.Running;
  },
  set(value: boolean) {
    if (value) {
      service.restartService();
    } else {
      service.stopService();
    }
  },
});

async function howToJoin() {
  const dialog = useDialogStore();
  const dialogComponentMap = {
    Minecraft: () => import("../../components/dialogs/minecraft/DialogJoin.vue"),
    Nightingale: () => import("../../components/dialogs/nightingale/DialogJoin.vue"),
    Hytale: () => import("../../components/dialogs/hytale/DialogJoin.vue"),
    StarRupture: () => import("../../components/dialogs/star_rupture/DialogJoin.vue"),
  };

  await dialog.createDialog({
    component: markRaw(defineAsyncComponent(dialogComponentMap[service.ctx.profile?.name])),
  });
}

async function selectBattlegroup() {
  const dialog = useDialogStore();
  await dialog.createDialog({
    component: markRaw(defineAsyncComponent(() => import("../dialogs/dune/DialogBlock.vue"))),
  });
}

async function extra() {
  if (extraHeaderButton.value) {
    extraHeaderButton.value?.btnFn();
  }
}

const webDashboardPort = computed(() => service.ctx.profile?.displayPorts?.webDashboardPort);
const webDashboardIp = computed(() => {
  return webDashboardPort.value ? `${accessData.value.split(":")[0]}:${webDashboardPort.value}` : null;
});

const gameServerState = computed(() => service.gameServerState);
const maxPlayers = computed(() => gameServerState.value?.maxPlayers);
const currentPlayers = computed(() => gameServerState.value?.currentPlayers);
const queried = computed(() => gameServerState.value?.queried);
const activeMap = computed(() => gameServerState.value?.map);

watch(
  () => route.query.edit,
  (newVal) => {
    if (newVal === undefined) {
      client.setIsInEditor(false);
    }
  },
);
</script>
